import { CreditSalesSlip, DeviceType, QRSalesSlip } from '@/apis/vescajs'
import type { SelfCheckinCompleteState } from '@/components/pages/self-checkin/complete'
import { CASH, CASH_CHANGER, CREDIT_CARD } from '@/models/payment-method'
import type { CheckinPrint, FourLanguageText } from '@/models/self-checkin/checkin-print'
import type { MultilingualText } from '@/models/survey/types'

// 紙1枚に出す値。出さないものは null（一覧なら空の配列）にし、描画する側は null かどうかだけを見る
export type CheckinReceiptSheet = {
  sheetNumber: number
  heading: { logoImage: string; title: null } | { logoImage: null; title: 'accommodation' }
  room: { number: string; key: string | null; qrCode: string | null; cardReplacementDate: string | null } | null
  stay: {
    guestName: string
    paxOfAdults: number
    paxOfChildren: number
    checkinDate: string
    checkoutDate: string
    plan: FourLanguageText | null
    timeSelections: Array<{ title: MultilingualText; from: string[] }>
  }
  hotelComment: FourLanguageText
  payment: CheckinReceiptPayment | null
  guestAppUrl: string
}

export type CheckinReceiptPayment = {
  receipt: {
    registrationNumber: string | null
    addressee: string
    paymentAmount: number
    totalAmountAt10Per: number
    totalTaxAt10Per: number
    totalAmountAt8Per: number
    totalTaxAt8Per: number
    provisoMethod: { type: 'creditCard' } | { type: 'cash' } | { type: 'brand'; brandName: string }
    taxOfficeName: string | null
    hotel: { name: FourLanguageText; address: FourLanguageText }
    salesSlip: CreditSalesSlip | QRSalesSlip | null
  } | null
  usageAmount: number | null
}

type CompletePaymentState = Pick<SelfCheckinCompleteState, 'payment' | 'paymentMethod' | 'confirmUsageTotalAmount'>
type CheckinPrintReservation = CheckinPrint['reservations'][number]
type CheckinPrintRoom = CheckinPrintReservation['rooms'][number]

export const buildCheckinReceiptSheets = (params: {
  print: CheckinPrint
  complete: CompletePaymentState
  isIssueRoomKey: boolean
}): CheckinReceiptSheet[] => {
  const { print, complete, isIssueRoomKey } = params

  const sheetContents = print.reservations.flatMap((reservation): Array<Pick<CheckinReceiptSheet, 'room' | 'stay'>> => {
    // 部屋番号が決まっている部屋が無い予約でも宿泊情報と領収書を出すため、部屋の枠が無い紙を1枚出す
    if (reservation.rooms.length === 0) {
      return [{ room: null, stay: buildStay(print, reservation, null) }]
    }
    return reservation.rooms.map(room => ({ room: buildRoom(room, isIssueRoomKey), stay: buildStay(print, reservation, room) }))
  })

  // 部屋ごとに渡す紙をどれも同じ形の先頭にするため、1枚目かどうかで分けない
  const heading = buildHeading(print.hotel.receiptLogoImage)

  return sheetContents.map(({ room, stay }, index): CheckinReceiptSheet => {
    const isFirstSheet = index === 0
    return {
      sheetNumber: index + 1,
      heading,
      room,
      stay,
      hotelComment: print.hotel.comment,
      // 支払いはチェックイン全体で1回なので、複数の予約をまとめても1枚目だけに出す
      payment: isFirstSheet ? buildPayment(print, complete) : null,
      guestAppUrl: print.guestAppUrl,
    }
  })
}

const buildHeading = (logoImage: string | null): CheckinReceiptSheet['heading'] =>
  logoImage ? { logoImage, title: null } : { logoImage: null, title: 'accommodation' }

const buildRoom = (room: CheckinPrintRoom, isIssueRoomKey: boolean): NonNullable<CheckinReceiptSheet['room']> => {
  const qrCode = room.qrCode || null
  // 部屋ごとの紙に分ける前から、QR コードがある部屋にはカード交換のお知らせを出していないため、その動きを引き継ぐ
  const shouldNoticeCardReplacement = isIssueRoomKey && !qrCode && room.isCardReplacementRequired
  return {
    number: room.number,
    key: room.key || null,
    qrCode,
    cardReplacementDate: shouldNoticeCardReplacement ? room.checkinDate : null,
  }
}

const buildStay = (
  print: CheckinPrint,
  reservation: CheckinPrintReservation,
  room: CheckinPrintRoom | null,
): CheckinReceiptSheet['stay'] => {
  const stayPeriod =
    room?.checkinDate && room.checkoutDate
      ? { checkinDate: room.checkinDate, checkoutDate: room.checkoutDate }
      : { checkinDate: reservation.checkinAt, checkoutDate: reservation.checkoutAt }
  return {
    guestName: reservation.guestName,
    paxOfAdults: room ? room.paxOfAdults : reservation.paxOfAdults,
    paxOfChildren: room ? room.paxOfChildren : reservation.paxOfChildren,
    checkinDate: stayPeriod.checkinDate,
    checkoutDate: stayPeriod.checkoutDate,
    plan: reservation.plan.ja ? reservation.plan : null,
    timeSelections: (print.spaces[reservation.reservationId] ?? []).map(group => ({
      title: group[0].title,
      from: group.map(({ from }) => from),
    })),
  }
}

const buildPayment = (print: CheckinPrint, complete: CompletePaymentState): CheckinReceiptPayment => ({
  receipt: buildReceipt(print, complete),
  usageAmount: complete.paymentMethod === CASH && complete.confirmUsageTotalAmount > 0 ? complete.confirmUsageTotalAmount : null,
})

const buildReceipt = (print: CheckinPrint, complete: CompletePaymentState): CheckinReceiptPayment['receipt'] => {
  const { payment, paymentMethod } = complete
  const isPaidAtTablet = paymentMethod === CREDIT_CARD || paymentMethod === CASH_CHANGER
  if (!payment || !isPaidAtTablet || payment.paymentAmount <= 0) {
    return null
  }
  return {
    registrationNumber: print.hotel.receiptRegistrationNumber,
    addressee: payment.receiptName || print.guestName,
    paymentAmount: payment.paymentAmount,
    totalAmountAt10Per: payment.totalAmountAt10Per,
    totalTaxAt10Per: payment.totalTaxAt10Per,
    totalAmountAt8Per: payment.totalAmountAt8Per,
    totalTaxAt8Per: payment.totalTaxAt8Per,
    provisoMethod: toProvisoMethod(payment, paymentMethod),
    taxOfficeName: print.hotel.receiptTaxOfficeName,
    hotel: { name: print.hotel.name, address: print.hotel.address },
    salesSlip: payment.salesSlip ?? null,
  }
}

const toProvisoMethod = (
  payment: NonNullable<CompletePaymentState['payment']>,
  paymentMethod: CompletePaymentState['paymentMethod'],
): NonNullable<CheckinReceiptPayment['receipt']>['provisoMethod'] => {
  if (payment.deviceType === DeviceType.PaidQr) {
    return { type: 'brand', brandName: payment.brandName }
  }
  if (paymentMethod === CASH_CHANGER) {
    return { type: 'cash' }
  }
  return { type: 'creditCard' }
}
